import React from 'react';

// Trading-terminal sector labels — mirrors what the user called out:
// aviation / maritime / logistics / utilities / telecom / energy /
// public safety / trade / security. Anything else Atlas returns via
// domain_coverage gets its own tile automatically.
const SECTOR_LABELS = {
  aviation: 'AVIATION',
  maritime: 'MARITIME',
  shipping_chokepoints: 'MARITIME',
  logistics: 'LOGISTICS',
  utilities: 'UTILITIES',
  power_grid: 'UTILITIES',
  telecom: 'TELECOM',
  energy_commodities: 'ENERGY',
  energy: 'ENERGY',
  public_safety: 'SAFETY',
  security_conflict: 'SECURITY',
  trade_sanctions: 'TRADE',
  markets: 'MARKETS',
  geopolitics: 'GEO',
  macroeconomics: 'MACRO',
  institutions_policy: 'POLICY',
};

function severityClass(strain) {
  if (strain >= 0.75) return 'is-critical';
  if (strain >= 0.5) return 'is-elevated';
  return '';
}

function deltaClass(series) {
  if (!series || series.length < 2) return 'flat';
  const [prev, cur] = [series[series.length - 2], series[series.length - 1]];
  if (cur > prev + 0.02) return 'down';  // higher strain = bad = red
  if (cur < prev - 0.02) return 'up';    // lower strain = good = green
  return 'flat';
}

function deltaPct(series) {
  if (!series || series.length < 2) return '0.00%';
  const [prev, cur] = [series[series.length - 2], series[series.length - 1]];
  const pct = ((cur - prev) * 100).toFixed(2);
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct}%`;
}

export default function SectorHeatmapGrid({ domainCoverage, history, insights, selectedSector, onSelectSector }) {
  const entries = Object.entries(domainCoverage || {});

  // Pre-bucket insight count per sector for the "signal volume" column.
  const countBySector = {};
  (insights || []).forEach((i) => {
    const d = (i.domain || i.sector || '').toLowerCase();
    if (!d) return;
    countBySector[d] = (countBySector[d] || 0) + 1;
  });

  if (entries.length === 0) {
    return <div className="atl-empty" data-testid="sector-grid-empty">No sector telemetry</div>;
  }

  return (
    <div className="sector-grid" data-testid="sector-grid">
      {entries.map(([key, raw]) => {
        const cov = typeof raw === 'number' ? { strain: raw } : (raw || {});
        const strain = Number(cov.strain ?? cov.value ?? cov.coverage ?? 0);
        const series = history?.[key] || [strain];
        const sevCls = severityClass(strain);
        const dCls = deltaClass(series);
        const dPct = deltaPct(series);
        const active = selectedSector === key ? 'is-active' : '';
        const volume = countBySector[key] ?? 0;
        const label = SECTOR_LABELS[key] || key.replace(/_/g, ' ').toUpperCase();

        return (
          <button
            key={key}
            className={`sector-tile ${sevCls} ${active}`}
            onClick={() => onSelectSector && onSelectSector(selectedSector === key ? null : key)}
            data-testid={`sector-tile-${key}`}
          >
            <div className="h">
              <span className="name">{label}</span>
              <span className="studio-chip" style={{ padding: '1px 4px', fontSize: 9 }}>{volume}</span>
            </div>
            <div className="strain" data-testid={`sector-strain-${key}`}>{(strain * 100).toFixed(1)}</div>
            <div className={`delta ${dCls}`} data-testid={`sector-delta-${key}`}>{dPct}</div>
            <div className="mini" aria-hidden="true">
              {series.slice(-12).map((v, idx) => (
                <i
                  key={idx}
                  style={{
                    height: `${Math.max(8, Math.min(100, v * 100))}%`,
                    background: v >= 0.75 ? 'var(--status-critical)'
                              : v >= 0.5 ? 'var(--status-elevated)'
                              : 'var(--status-nominal)',
                  }}
                />
              ))}
            </div>
          </button>
        );
      })}
    </div>
  );
}
